import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apiClient } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { CallbackForm } from '@/components/partners/CallbackForm';
import { ShareButtons } from '@/components/partners/ShareButtons';
import { formatINR } from '@/components/partners/utils';
import { extractPartnerId } from '@/utils/slug';
import {
  Star, MapPin, Clock, Phone, Mail, CheckCircle,
  ArrowLeft, ShieldCheck, Briefcase, Sparkles,
} from 'lucide-react';

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-xl border border-white/15">
    <Icon className="w-4 h-4 text-purple-300" />
    <div className="leading-tight">
      <p className="text-sm font-semibold text-white">{value}</p>
      <p className="text-[11px] text-purple-200/80">{label}</p>
    </div>
  </div>
);

export default function VendorProfile() {
  const { slugId, partnerId } = useParams();
  const id = extractPartnerId(slugId || partnerId);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get(`/services/vendor/${id}`);
        setVendor(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Vendor not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-4"><div className="h-96 bg-gray-200 rounded-2xl animate-pulse" /></div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-gray-500 mb-4" data-testid="vendor-not-found">{error || 'Vendor not found'}</p>
          <Link to="/services/priest"><Button variant="outline">Back to Services</Button></Link>
        </div>
      </div>
    );
  }

  const stats = vendor.stats || {};
  const activeServices = (vendor.services || []).filter((s) => s.is_active);
  const title = `${vendor.name}${vendor.company_name ? ` · ${vendor.company_name}` : ''} — Verified Service Provider`;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="vendor-profile-page">
      <Helmet>
        <title>{title} | ZenStudios</title>
        <meta name="description" content={(vendor.bio || `${vendor.name} is a verified service provider in ${vendor.location?.city}. View services and request a callback.`).slice(0, 160)} />
        <meta property="og:title" content={title} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <section className="bg-gradient-to-br from-purple-950 via-violet-900 to-indigo-900 text-white">
        <div className="max-w-5xl mx-auto px-4 pt-24 pb-12">
          <div className="flex items-center justify-between gap-3 mb-8">
            <Link to="/services/priest" className="inline-flex items-center gap-1 text-purple-300 hover:text-white text-sm transition-colors" data-testid="vendor-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Vendors
            </Link>
            <ShareButtons title={vendor.name} testidPrefix="vendor-share" />
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-24 h-24 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center text-4xl font-bold flex-shrink-0">
              {vendor.name?.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold" data-testid="vendor-name">{vendor.name}</h1>
                {vendor.verification?.status === 'verified' && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-200 bg-purple-500/20 px-3 py-1 rounded-full" data-testid="vendor-verified-badge">
                    <ShieldCheck className="w-3.5 h-3.5" /> Verified
                  </span>
                )}
              </div>
              {vendor.company_name && <p className="text-purple-200 mt-1 flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {vendor.company_name}</p>}
              <p className="text-purple-200/80 mt-1 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {[vendor.location?.city, vendor.location?.state].filter(Boolean).join(', ')}</p>
              <div className="flex flex-wrap gap-3 mt-5">
                <StatPill icon={Star} label="Average rating" value={stats.avg_rating || 'New'} />
                <StatPill icon={CheckCircle} label="Reviews" value={stats.total_reviews || 0} />
                <StatPill icon={Sparkles} label="Services offered" value={activeServices.length} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {vendor.bio && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="vendor-about">
              <h2 className="font-semibold text-gray-900 mb-3">About</h2>
              <p className="text-gray-600 leading-relaxed">{vendor.bio}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="vendor-services">
            <h2 className="font-semibold text-gray-900 mb-4">Services Offered</h2>
            {activeServices.length === 0 ? (
              <p className="text-sm text-gray-400" data-testid="vendor-no-services">No services listed yet.</p>
            ) : (
              <div className="space-y-3">
                {activeServices.map((svc) => (
                  <div key={svc.service_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-medium text-gray-900">{svc.name}</p>
                      {svc.description && <p className="text-sm text-gray-500">{svc.description}</p>}
                      {svc.duration_hours ? (
                        <span className="flex items-center gap-1 mt-1 text-xs text-gray-400"><Clock className="w-3 h-3" /> {svc.duration_hours}h</span>
                      ) : null}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatINR(svc.price) || 'On request'}</p>
                      {svc.price_type && <p className="text-xs text-gray-500">{svc.price_type}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {vendor.location?.localities?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="vendor-location">
              <h2 className="font-semibold text-gray-900 mb-3">Service Areas</h2>
              <div className="flex flex-wrap gap-2">
                {vendor.location.localities.map((loc) => (
                  <span key={loc} className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">{loc}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 lg:sticky lg:top-24 space-y-4" data-testid="vendor-contact-card">
            <h3 className="font-semibold text-gray-900">Interested in this vendor?</h3>
            <CallbackForm
              endpoint={`/public/vendor/${id}/callback`}
              accent="purple"
              testidPrefix="vendor-callback"
            />
            {(vendor.phone || vendor.email) && (
              <div className="pt-4 border-t border-gray-100 space-y-2 text-sm">
                {vendor.phone && <a href={`tel:${vendor.phone}`} className="flex items-center gap-2 text-gray-600 hover:text-purple-600" data-testid="vendor-phone-link"><Phone className="w-4 h-4 text-purple-500" /> {vendor.phone}</a>}
                {vendor.email && <a href={`mailto:${vendor.email}`} className="flex items-center gap-2 text-gray-600 hover:text-purple-600" data-testid="vendor-email-link"><Mail className="w-4 h-4 text-purple-500" /> {vendor.email}</a>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
